import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { registerExternalFontFaceDeclarations, useAppDispatch } from "../../../../store";
import { useDebounce } from "../../../../hooks/useDebounce";

import manualIframe from "./manualIframe";
import loadFontFacesFromStyleElement from "./loadFontFacesFromStyleElement";
import { ExternalFontSubForm, SubFormProps } from "./ExternalFontSubForm";

function useEmbedQueryOptions(iframeURL: string) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return {
    queryKey: useDebounce(['EditStyle/EmbedURL', iframeURL], 250),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      manualIframe(iframeURL, queryClient, signal).then((result) => {
        if (!result) return null;
        const { doc, proxyAuthToken } = result;

        return Promise.all(
          [...doc.querySelectorAll('style')].map(
            (el) => loadFontFacesFromStyleElement(el, iframeURL, proxyAuthToken).then((map) => {
              dispatch(registerExternalFontFaceDeclarations(map));
              return map;
            }),
          )
        );
      }),
  };
}

export function EmbedURLImpl({
  setSubFormNotEmpty,
  state: {
    iframeURL,
    setIframeURL,
  },
}: SubFormProps<{
  iframeURL: string,
  setIframeURL: Dispatch<SetStateAction<string>>
}>) {

  useQuery(useEmbedQueryOptions(iframeURL));

  useEffect(() => {
    setSubFormNotEmpty(iframeURL !== '');
  }, [iframeURL, setSubFormNotEmpty]);

  return (
    <>
      <div
        title="The URL of the page where the Interactive Map will be embedded, used to extract it's fonts"
      >
        <label
          htmlFor="edit-styles-embed-url-input"
        >
          Embed URL
        </label>
        <input
          id="edit-styles-embed-url-input"
          type="text"
          value={iframeURL}
          onChange={(e) => setIframeURL(e.target.value)}
          className="w-100"
        />
      </div>
      <p>
        Enter a URL from which we will extract fonts. Typically, the URL where the map will be shown
      </p>
      <p role="alert">
        <FontAwesomeIcon icon={faTriangleExclamation} />&nbsp;
        Note: selecting a font this way requires that that font file extracted from the page remain available in the same location,
        meaning that if the font used on that page changes, you may need to update it here too
      </p>
    </>
  );
}

export function EmbedURL({ visible }: { visible: boolean }) {
  const [iframeURL, setIframeURL] = useState('');

  return (
    <ExternalFontSubForm
      visible={visible}
      subform={EmbedURLImpl}
      subformState={{ iframeURL, setIframeURL }}
      queryOptions={useEmbedQueryOptions(iframeURL)}
    />
  );
}
